import { motion } from 'framer-motion';
import React, { useEffect, useState, useRef } from "react";
import Slider from 'react-slick';
import ImageAutomation from "../../images/automation-fp.svg";
import ImageSupplyOrders from "../../images/automated-supply-re-orders.png";
import ImageSaleFaster from "../../images/closing-more-sale-faster.png";
import IconDevOpsAutomation from "../../icons/icon-devops-automation-fp.svg";
import IconDistributedDesign from "../../icons/icon-distributed-design.svg";
import IconEnterpriseIntegrations from "../../icons/icon-enterprise-integrations.svg";
import IconSecureDesign from "../../icons/icon-secure-design.svg";
import IconTechBackbone from "../../icons/icon-tech-backbone.svg";
import InView from "react-intersection-observer";
import HeaderAnim from "../common/utils/animations/textRevealAnimation";
import { SlideInWithScroll } from "../common/utils/animations/slideInWithScroll";
import Parallax from '../common/utils/animations/parallax';
import LazyLoad from 'react-lazyload';

export default function FlexprintCsAutomation(props) {
  const [isMobile, setisMobile] = useState(false);
  const [isVisible, setisVisible] = useState({ top: false, middle: false, bottom: false });
  const [elementTop, setElementTop] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const ref = useRef();

  const setValues = () => {
    setElementTop(ref.current.offsetTop);
    setClientHeight(window.innerHeight);
    setisMobile(window.innerWidth <= 1023);
  };

  useEffect(() => {
    if (!ref.current) return;
    props.isLoaded && setValues();
    window.addEventListener('resize', setValues);
    document.addEventListener('load', setValues);
    return () => {
      window.removeEventListener('resize', setValues);
      document.removeEventListener('load', setValues);
    };
  });

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 5000,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const sliderItems = [
    {
      icon: <IconDevOpsAutomation />,
      title: "DevOps Automation",
      content: "We implemented automated DevOps that offers reliability & reduce overhead."
    }, {
      icon: <IconDistributedDesign />,
      title: "DISTRIBUTED DESIGN",
      content: "Architecture is designed to scale up & down horizontally and handle failures gracefully."
    }, {
      icon: <IconTechBackbone />,
      title: "TECH BACKBONE",
      content: "The use of a next-generation approach and agile methodology makes it a better and faster solution."
    }, {
      icon: <IconEnterpriseIntegrations />,
      title: "Enterprise Integrations",
      content: "We integrated the IoT platform with existing apps such as SSO, ERP, and CRMs."
    }, {
      icon: <IconSecureDesign />,
      title: "SECURE BY DESIGN",
      content: "Security patterns implemented for enforcing the necessary authentication, confidentiality, and data integrity."
    }
  ];

  const triggers = [[0.9, 0.8], [0.85, 0.75], [0.7, 0.6], [0.65, 0.55], [0.5, 0.4]];
  return (
    <div className="cs-devops-section section-gap cs-automation" onLoad={setValues}>
      <div className="automation-top section-gap-large">
        <InView as="div" className="container" onChange={(inView) => setisVisible((prev) => { return { ...prev, top: inView }; })}>
          <div className="row d-flex flex-wrap">
            <div className="col cs-devops-content">
              <h2 className="theme-color-title dark-purple-color">{isVisible.top ? <HeaderAnim header="CLOUD & DEVOPS AUTOMATION" /> : ""}</h2>
              <h3>Building & scaling on cloud</h3>
              <p>We designed and developed a cloud-first platform on Amazon AWS that was able to generate machine intelligence in real-time and linearly scale to support the massive volumes. The platform architecture was designed to be fully distributed to scale up and down on-demand.</p>
            </div>

            <div className="col cs-devops-image">
              <LazyLoad>
                <img src={ImageAutomation} className="cb-devops-automation" title="Building and Scaling on Cloud" alt="building and scaling on cloud" />
              </LazyLoad>
            </div>
          </div>

          <div className="grid-layout-section-style-2">
            <div ref={ref} className="row d-flex flex-wrap">
              {isMobile && <Slider {...settings}>
                {
                  sliderItems.map((item, i) => {
                    const jsx = <motion.div className="col grid-item-style-2" key={i}>
                      <div className="item-inner">
                        <div className="grid-icon-left">
                          <i className="icon-small">{item.icon}</i>
                        </div>
                        <h4 className="h3">{item.title}</h4>
                        <p>{item.content}</p>
                      </div>
                    </motion.div>;

                    return <SlideInWithScroll elementTop={elementTop} clientHeight={clientHeight} triggerPoints={triggers[i]} children={jsx} key={i} />;
                  })
                }
              </Slider>}
              {!isMobile && <>
                {
                  sliderItems.map((item, i) => {
                    return <Parallax classes="col grid-item-style-2" key={i} id={"grid-item-automation" + i} triggers={[1, 0.7]} length={50} opacity={true}>
                      <div className="item-inner">
                        <div className="grid-icon-left">
                          <i className="icon-small">{item.icon}</i>
                        </div>
                        <h4 className="h3">{item.title}</h4>
                        <p>{item.content}</p>
                      </div>
                    </Parallax>;
                  })
                }
              </>}
            </div>
          </div>
        </InView>
      </div>

      <InView as="div" className="supply-automation-middle section-gap-large" onChange={(inView) => setisVisible((prev) => { return { ...prev, middle: inView }; })}>
        <div className="container">
          <div className="cs-supply-automation-content section-gap-small">
            <h2 className="theme-color-title dark-purple-color">{isVisible.middle ? <HeaderAnim header="SUPPLY AUTOMATION APP" /> : ""}</h2>
            <h3>Automated Supply Re-orders</h3>

            <p>Every time a printer runs out of ink, the customers had to re-order it manually. The client’s team had to manually operate the fulfillment process - from sales order to purchase order to delivery and installation. The process was error-prone, inefficient, and did not allow predictable supply management. </p>

            <p>Together with the client team, we came up with an idea to develop a supply automation engine that would automatically re-order ink and toners, generate supply orders, purchase orders, and ship the supplies to customers - no human input required.</p>
          </div>
          <div className="cs-supply-automation-image">
            <Parallax id="cs-supply-automation-image-anim" triggers={[1, 0.7]} length={50} opacity={true}>
              <motion.img src={ImageSupplyOrders} className="cb-devops-automation" title="Automated Supply Re-orders" alt="automated supply re-orders" />
            </Parallax>
            <small>360 VIEW OF EACH PRINTER FROM LOGS & ANALYTICS</small>
          </div>
        </div>
      </InView>
      <div className="grediant-text section-gap-large">
        <div className="container">
          <h2>Automated replenishments delivered the right sized supplies at the right time to the right customers - reducing costs and increasing efficiencies.</h2>
        </div>
      </div>
      <InView as="div" className="deal-dash-bottom" onChange={(inView) => setisVisible((prev) => { return { ...prev, bottom: inView }; })}>
        <div className="container">
          <div className="cs-deal-dash-content section-gap-small">
            <h2 className="theme-color-title dark-purple-color">{isVisible.bottom ? <HeaderAnim header="DEAL Dash APP" /> : ""}</h2>
            <h3>Closing More Sales, Faster</h3>
            <p>The client wanted to streamline its manually operated sales process to ensure the faster deal closure while improving productivity. We took an interactive approach with a long term vision and designed an automated platform to be modular, extendable, and intuitive that align with the client’s expected business outcomes. Rapidops team developed an intuitive dashboard for deal automation by taking advantage of smart functionality and workflows.</p>
          </div>
          <div className="cs-deal-dash-image">
            <Parallax id="cs-deal-dash-image-anim" triggers={[1, 0.7]} length={80} opacity={true}>
              <motion.img src={ImageSaleFaster} className="cb-devops-automation" title="Deal Dashboard" alt="Deal Dashboard" />
            </Parallax>
            <small>DEAL AUTOMATION APP SCREEN SHOT</small>
          </div>
        </div>
      </InView>
    </div>
  );
}